// @seventide/types — platform area: rules, rule-set history and the rules admin (R-5.6, RE-1, FR-12.1).
// Re-exported by ../platform.ts. Names are prefixed (RuleSetHistory*, AdminRuleSet*, RuleSetDiff*) to avoid
// collisions with RuleSetResponse (auth.ts) and with the RuleSet type exported by @seventide/rules.
import { z } from 'zod';
import { IsoDate, IsoDateTime } from '../common';
import { Nation } from '../enums';

// ---------------------------------------------------------------- public history ("What changed in the rules")

/** Where a rule set comes from: shipped inside the app (bundled) or published by an admin (database). */
export const RuleSetOrigin = z.enum(['bundled', 'published']);
export type RuleSetOrigin = z.infer<typeof RuleSetOrigin>;

export const RuleSetHistoryQuery = z.object({ nation: Nation.default('england') });
export type RuleSetHistoryQuery = z.infer<typeof RuleSetHistoryQuery>;

export const RuleSetHistoryEntry = z.object({
  version: z.string(),
  nation: Nation,
  taxYear: z.string(),
  effectiveFrom: IsoDate,
  verifiedOn: IsoDate.nullable(),
  origin: RuleSetOrigin,
  /** 'withdrawn' = a published update that a platform admin later retired. */
  state: z.enum(['active', 'superseded', 'scheduled', 'withdrawn']),
  /** Plain-English summary of what changed (published updates) or the rule set's notes (bundled). */
  changes: z.array(z.string()),
  publishedAt: IsoDateTime.nullable(),
  withdrawnAt: IsoDateTime.nullable(),
});
export type RuleSetHistoryEntry = z.infer<typeof RuleSetHistoryEntry>;

export const RuleSetHistoryResponse = z.object({
  nation: Nation,
  /** Version currently in force for this nation (today, UK time). */
  currentVersion: z.string(),
  entries: z.array(RuleSetHistoryEntry),
});
export type RuleSetHistoryResponse = z.infer<typeof RuleSetHistoryResponse>;

// ---------------------------------------------------------------- admin: validation, diff

/** One validation problem with a rule-set JSON document. `path` is the dotted location, e.g. values.iht.petYears. */
export const AdminRuleSetIssue = z.object({ path: z.string(), message: z.string() });
export type AdminRuleSetIssue = z.infer<typeof AdminRuleSetIssue>;

export const RuleSetDiffKind = z.enum(['added', 'removed', 'changed']);
export type RuleSetDiffKind = z.infer<typeof RuleSetDiffKind>;

/** A changed leaf in the rule set. Values are JSON leaves (number, string, boolean, null, or [] / {}). */
export const RuleSetDiffEntry = z.object({
  path: z.string(),
  kind: RuleSetDiffKind,
  oldValue: z.unknown().optional(),
  newValue: z.unknown().optional(),
});
export type RuleSetDiffEntry = z.infer<typeof RuleSetDiffEntry>;

export const RuleSetDiff = z.object({
  /** The set compared against: the one in force today for the draft's nation. */
  against: z.object({ version: z.string(), nation: Nation, origin: RuleSetOrigin }),
  entries: z.array(RuleSetDiffEntry),
  counts: z.object({ added: z.number().int(), removed: z.number().int(), changed: z.number().int() }),
  /** Whether publishing this set would make it the active set today (tie-break: latest effectiveFrom, then version). */
  wouldBecomeActiveToday: z.boolean(),
  /** Plain-English note on when the set would take effect. */
  activationNote: z.string(),
});
export type RuleSetDiff = z.infer<typeof RuleSetDiff>;

/** Body for validate / create draft / update draft. `ruleSet` may be the parsed object or the raw JSON text. */
export const AdminRuleSetDraftRequest = z.object({
  ruleSet: z.union([z.string().max(500_000), z.record(z.string(), z.unknown())]),
  changelog: z.string().trim().max(2000).optional(),
});
export type AdminRuleSetDraftRequest = z.infer<typeof AdminRuleSetDraftRequest>;

export const AdminRuleSetValidateResponse = z.object({
  valid: z.boolean(),
  issues: z.array(AdminRuleSetIssue),
  /** Present when valid. */
  summary: z
    .object({ version: z.string(), nation: Nation, taxYear: z.string(), effectiveFrom: IsoDate, verifiedOn: IsoDate })
    .nullable(),
  diff: RuleSetDiff.nullable(),
});
export type AdminRuleSetValidateResponse = z.infer<typeof AdminRuleSetValidateResponse>;

export const AdminRuleSetPublishRequest = z.object({
  /** Shown to every family under "What changed in the rules". One change per line. */
  changelog: z
    .string()
    .trim()
    .min(10, 'Describe what changed in at least 10 characters')
    .max(2000, 'Keep the change note under 2,000 characters'),
});
export type AdminRuleSetPublishRequest = z.infer<typeof AdminRuleSetPublishRequest>;

export const AdminRuleSetRetireRequest = z.object({
  reason: z
    .string()
    .trim()
    .min(5, 'Give a short reason (at least 5 characters)')
    .max(500, 'Keep the reason under 500 characters'),
});
export type AdminRuleSetRetireRequest = z.infer<typeof AdminRuleSetRetireRequest>;

// ---------------------------------------------------------------- admin: versions

export const AdminRuleSetStatus = z.enum(['bundled', 'draft', 'published', 'retired']);
export type AdminRuleSetStatus = z.infer<typeof AdminRuleSetStatus>;

/** Bundled ids look like `bundled:2026-27.1:england`; database rows use their uuid. */
export const AdminRuleSetId = z
  .string()
  .regex(
    /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|bundled:\d{4}-\d{2}\.\d+:(england|scotland|wales|northern_ireland))$/i,
    'Unknown rule-set id',
  );

export const AdminRuleSetSummary = z.object({
  id: z.string(),
  version: z.string(),
  nation: Nation,
  taxYear: z.string(),
  effectiveFrom: IsoDate,
  verifiedOn: IsoDate.nullable(),
  status: AdminRuleSetStatus,
  /** True for the set in force today for its nation. */
  active: z.boolean(),
  changelog: z.string().nullable(),
  createdAt: IsoDateTime.nullable(),
  updatedAt: IsoDateTime.nullable(),
  publishedAt: IsoDateTime.nullable(),
  publishedByName: z.string().nullable(),
  retiredAt: IsoDateTime.nullable(),
  retiredReason: z.string().nullable(),
});
export type AdminRuleSetSummary = z.infer<typeof AdminRuleSetSummary>;

export const AdminRuleSetListQuery = z.object({ nation: Nation.optional() });
export type AdminRuleSetListQuery = z.infer<typeof AdminRuleSetListQuery>;

export const AdminRuleSetListResponse = z.object({
  items: z.array(AdminRuleSetSummary),
  /** Active version per nation today. */
  active: z.record(z.string(), z.object({ version: z.string(), origin: RuleSetOrigin })),
});
export type AdminRuleSetListResponse = z.infer<typeof AdminRuleSetListResponse>;

export const AdminRuleSetEventDto = z.object({
  id: z.string(),
  action: z.enum(['draft_created', 'draft_updated', 'draft_deleted', 'published', 'retired']),
  summary: z.string(),
  actorName: z.string().nullable(),
  at: IsoDateTime,
});
export type AdminRuleSetEventDto = z.infer<typeof AdminRuleSetEventDto>;

export const AdminRuleSetDetail = AdminRuleSetSummary.extend({
  /** The rule-set JSON (parsed). */
  ruleSet: z.record(z.string(), z.unknown()),
  /** Canonical JSON (the signing payload). */
  payload: z.string(),
  /** base64 Ed25519 signature stored at publish time; null for drafts and bundled sets. */
  signature: z.string().nullable(),
  events: z.array(AdminRuleSetEventDto),
});
export type AdminRuleSetDetail = z.infer<typeof AdminRuleSetDetail>;
